import { Device, Trip, User } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { endOfDay } from "date-fns/endOfDay";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsPopup from "./components/filter/SettingsPopup";
import { TripDetailsErrorLines } from "./components/TripDetailsErrorLines";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import { useFilteredTrips } from "@/hooks/trips/useFilteredTrips";
import { useTripsByDateRange } from "@/hooks/trips/useTripsByDateRange";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";
import { mapLazy } from "@/utilities/mapLazy";

const TripsGrid = mapLazy("TripsGrid", import("./components/TripsGrid"));
const ReasonInput = mapLazy("ReasonInput", import("./components/filter/ReasonInput"));
const DateRangeSelect = mapLazy("DateRangeSelect", import("@/components/input/DateRangeSelect"));
const DriverOrDeviceSelect = mapLazy(
  "DriverOrDeviceSelect",
  import("@/components/input/DriverOrDeviceSelect"),
);
const ReportDownloadButton = mapLazy(
  "ReportDownloadButton",
  import("./components/ReportDownloadButton"),
);

export default function LogbookEditPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [onlyDrivers, setOnlyDrivers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState(true);

  // This page used to store its range under the fault overview's keys, so changing the range
  // here silently changed it there and the other way round. It now has its own, per user.
  const { fromDate, toDate, setRange, isReady } = usePersistedDateRange({
    key: "dateRange:logbookEdit",
    defaultLookbackDays: 1,
  });

  const [selectedTrips, setSelectedTrips] = useState<Trip[]>([]);

  const {
    data: trips,
    isLoading,
    isError,
    error,
    detailsPending,
    detailsError,
    detailsErrorSource,
    annotationsError,
    progress,
    isPartial,
  } = useFilteredTrips({
    users,
    devices,
    toDate,
    fromDate,
    reasons,
    enabled: isReady,
    // A year of a whole fleet takes a while; the rows show as they arrive instead of all at the end.
    progressive: true,
  });
  // Same query key as inside useFilteredTrips (deduplicated by TanStack Query); the
  // unfiltered list keeps "previous trip" lookups correct when a reason filter is active.
  const { data: allTrips } = useTripsByDateRange({
    users,
    devices,
    toDate,
    fromDate,
    enabled: isReady,
  });
  const { t } = useTranslation();
  // Rows whose existing reason was never read are locked (TripsGrid), so it cannot be overwritten unseen.
  const hasUnreadReasons = !!trips?.some(trip => trip.reason === null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              onlyDrivers={onlyDrivers}
              setOnlyDrivers={setOnlyDrivers}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              vehicleStatus={vehicleStatus}
              setVehicleStatus={setVehicleStatus}
            />
            <DriverOrDeviceSelect
              users={users}
              devices={devices}
              onChange={(users: User[], devices: Device[]) => {
                setUsers(users ?? []);
                setDevices(devices ?? []);
              }}
              onlyDrivers={onlyDrivers}
              showArchived={showArchived}
            />
          </Box>
          <ReasonInput onChange={setReasons} />
          <DateRangeSelect
            toDate={toDate}
            fromDate={fromDate}
            onChange={(from, to) => {
              setRange(from, to ?? (from ? endOfDay(from) : undefined));
            }}
          />
          <ReportDownloadButton
            fromDate={fromDate}
            toDate={toDate}
            deviceIds={devices.map(d => d.id)}
            userIds={users.map(u => u.id)}
          />
        </Box>
      }
      content={
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}>
          {progress && progress.total > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("logbookEdit.tripsProgress", {
                  done: progress.done,
                  total: progress.total,
                  trips: trips?.length ?? 0,
                })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(progress.done / progress.total) * 100}
              />
            </Box>
          )}
          {detailsError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <TripDetailsErrorLines source={detailsErrorSource} annotationsError={annotationsError} />
              {hasUnreadReasons && t("logbookEdit.unreadReasonsLocked")}
            </Alert>
          )}
          <TripsGrid
            trips={trips}
            addressTrips={allTrips}
            fromDate={fromDate}
            toDate={toDate}
            // Still loading while only part of the trips is in, so the export waits for all of them.
            isLoading={isLoading || isPartial || detailsPending}
            isError={isError || !!detailsError}
            error={error}
            selectedTrips={selectedTrips}
            onSelectedTripsChange={setSelectedTrips}
          />
        </Box>
      }
    />
  );
}
